import styled, { css, keyframes } from 'styled-components';

export type BannerArrowCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const ARROW_ROTATION: Record<BannerArrowCorner, number> = {
  'top-right': 0,
  'bottom-right': 90,
  'bottom-left': 180,
  'top-left': -90,
};

const ARROW_CORNER_STYLE: Record<BannerArrowCorner, string> = {
  'top-right': 'top: 8px; right: 8px;',
  'top-left': 'top: 8px; left: 8px;',
  'bottom-right': 'bottom: 8px; right: 8px;',
  'bottom-left': 'bottom: 8px; left: 8px;',
};

interface BannerAnimationProps {
  $backgroundColor: string;
  $borderColor: string;
  $borderWidth: number;
  $borderRadius: number;
  $width: number;
  $top?: number;
  $bottom?: number;
  $right: number;
  $entryPct: number;
  $visibleEndPct: number;
  $totalDuration: number;
  $fontSize: number;
}

const buildAnimation = ({
  $entryPct,
  $visibleEndPct,
  $totalDuration,
}: Pick<BannerAnimationProps, '$entryPct' | '$visibleEndPct' | '$totalDuration'>) => {
  const slide = keyframes`
    0% {
      transform: translateX(calc(100% + 60px));
      opacity: 0;
    }
    ${$entryPct}% {
      transform: translateX(0);
      opacity: 1;
    }
    ${$visibleEndPct}% {
      transform: translateX(0);
      opacity: 1;
    }
    100% {
      transform: translateX(calc(100% + 60px));
      opacity: 0;
    }
  `;

  return css`
    animation: ${slide} ${$totalDuration}ms ease-in-out forwards;
  `;
};

export const InfoBannerWrapper = styled.div<BannerAnimationProps>`
  position: fixed;
  ${({ $top, $bottom }) => ($top !== undefined ? `top: ${$top}px;` : `bottom: ${$bottom ?? 20}px;`)}
  right: ${({ $right }) => $right}px;
  isolation: isolate;
  width: ${({ $width }) => $width}px;
  background: ${({ $backgroundColor }) => $backgroundColor};
  border: ${({ $borderWidth }) => $borderWidth}px solid ${({ $borderColor }) => $borderColor};
  border-radius: ${({ $borderRadius }) => $borderRadius}px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  padding: 18px 20px;
  z-index: 9999;

  display: flex;
  align-items: center;
  gap: 15px;

  ${(props) =>
    buildAnimation({
      $entryPct: props.$entryPct,
      $visibleEndPct: props.$visibleEndPct,
      $totalDuration: props.$totalDuration,
    })}
`;

export const InfoBannerMessage = styled.div<{ $textColor: string; $fontSize: number }>`
  color: ${({ $textColor }) => $textColor};
  font-weight: 700;
  line-height: 1.4;
  font-size: ${({ $fontSize }) => $fontSize}px;
`;

export const InfoBannerIconWrapper = styled.div`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const InfoBannerArrow = styled.div<{ $corner: BannerArrowCorner }>`
  position: absolute;
  ${({ $corner }) => ARROW_CORNER_STYLE[$corner]}
  line-height: 0;

  svg {
    display: block;
    transform: rotate(${({ $corner }) => ARROW_ROTATION[$corner]}deg);
  }
`;
